import { IdlePeriodLegacySettingsFormModel, IdlePeriodSettingsFormModel } from '../models'
import { useContext, useEffect, useState } from 'react'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { isUndefined } from 'lodash'
import {
  getIdlePeriodsSettings,
  getIdlePeriodsLegacySettings,
  updateIdlePeriodsSettings,
  updateIdlePeriodsLegacySettings,
} from 'src/Libs/smart-charge/api'
import { IdlePeriodSettingsProxy } from 'src/Libs/smart-charge/types'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { humanTimeToSecondsFromMidnight } from '@e3dc-react/shell/Libs/date'
import { fromLegacyIdlePeriodDtoToModel } from 'src/Libs/smart-charge/transformers'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { InstallationOnlineStatusContext } from 'src/Libs/installations/context/installation-online-status-context'
import { useFarmContext } from 'src/Elements/Farm'

interface UseIdlePeriodDataHook {
  isLoading: boolean
  isError: boolean
  loadSettings: () => Promise<void>
  updateDefaultSettings: (model: IdlePeriodSettingsFormModel) => Promise<void>
  updateLegacySettings: (model: IdlePeriodLegacySettingsFormModel) => Promise<void>
  settings: IdlePeriodSettingsProxy | null
}

/**
 * Provide utils to access the data of the [Idle Period Settings API](storages/{sn}/smart-functions/smart-charge)
 *
 * IdlePeriod settings support the special case that they may have to support 2 different APIs.
 *
 * See (Smart Functions)[https://docs.e3dc.com/x/pQzIAw] for more details to this.
 *
 * @param storageSerialNr
 */
const useIdlePeriodData = (storageSerialNr?: string): UseIdlePeriodDataHook => {
  const authFetch = useAuthFetchWithResponse()
  const { enqueueErrorStack, snackError } = useSnackbar()
  const { t } = useCustomTranslation()
  const [settings, setSettings] = useState<IdlePeriodSettingsProxy | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)
  const { isServerOnline } = useContext(InstallationOnlineStatusContext)
  const { isFarm } = useFarmContext()
  const isServerOffline = isFarm && isServerOnline !== true

  const loadSettings = async (): Promise<void> => {
    if (isUndefined(storageSerialNr)) {
      throw new Error('missing storageSerialNr ')
    }

    const result = await getIdlePeriodsSettings(storageSerialNr, authFetch, !isServerOffline)
    if (!result.hasErrors && result.value) {
      setSettings({ value: result.value, isLegacy: false })
    }

    if (result.hasErrors && result.error) {
      loadLegacySettings().catch(snackError)
    }
  }

  /**
   * Provides IdlePeriod configuration settings from the legacy Smart Charge Idle Period API
   */
  const loadLegacySettings = async (): Promise<void> => {
    if (isUndefined(storageSerialNr)) {
      throw new Error('missing storageSerialNr ')
    }

    const result = await getIdlePeriodsLegacySettings(storageSerialNr, authFetch)

    if (!result.hasErrors && result.value) {
      setSettings({ value: result.value, isLegacy: true })
    }

    if (result.hasErrors && !result.error) {
      throw new Error('Unsupported API call')
    }
  }

  /**
   * Update the IdlePeriod configuration settings for the default Smart Charge Idle Period API
   *
   * @param model
   */
  const updateDefaultSettings = async (model: IdlePeriodSettingsFormModel): Promise<void> => {
    if (isUndefined(storageSerialNr)) {
      throw new Error(`Invalid activeStorageId  (id: ${storageSerialNr})`)
    }

    return updateIdlePeriodsSettings(storageSerialNr, model, authFetch).then(() => {
      if (!settings) {
        return
      }

      setSettings({
        isLegacy: settings.isLegacy,
        value: {
          emsGetIdlePeriodsEnable: model.emsGetIdlePeriodsEnable,
          emsIdlePeriod: model.emsIdlePeriod.map(formFields => ({
            isActive: formFields.isActive,
            start: humanTimeToSecondsFromMidnight(formFields.startTimeOfDay),
            stop: humanTimeToSecondsFromMidnight(formFields.endTimeOfDay),
            peridodType: formFields.type,
            weekdays: formFields.daysOfWeek,
            name: formFields.description,
          })),
        },
      })
    })
  }

  /**
   * Update the IdlePeriod configuration settings for the legacy Smart Charge Idle Period API
   * @param model
   */
  const updateLegacySettings = async (model: IdlePeriodLegacySettingsFormModel): Promise<void> => {
    if (isUndefined(storageSerialNr)) {
      throw new Error(`Invalid activeStorageId  (id: ${storageSerialNr})`)
    }

    return updateIdlePeriodsLegacySettings(storageSerialNr, model, authFetch).then(model => {
      if (settings) {
        setSettings({
          isLegacy: settings.isLegacy,
          value: fromLegacyIdlePeriodDtoToModel(model),
        })
      }
    })
  }

  useEffect(() => {
    setIsLoading(true)
    loadSettings()
      .catch((error: unknown) => {
        setIsError(true)
        console.error(error)
        enqueueErrorStack(t('settings.idle-period.load.error'))
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, []) // eslint-disable-line

  return { isLoading, isError, loadSettings, updateDefaultSettings, updateLegacySettings, settings }
}

export default useIdlePeriodData
